import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Gift, CheckCircle, XCircle, User, Store, ShoppingCart, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import LoadingState from '@/components/shared/LoadingState';
import ErrorState from '@/components/shared/ErrorState';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/api/apiClient';

const fmt2 = (n) =>
  Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATE_LABEL = { draft: 'مسودة', submitted: 'بانتظار الاعتماد', done: 'تم الصرف', rejected: 'مرفوض' };
const STATE_COLOR = {
  draft: 'bg-gray-100 text-gray-600 border-gray-200',
  submitted: 'bg-amber-50 text-amber-700 border-amber-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-semibold text-foreground truncate">{value}</span>
    </div>
  );
}

export default function GiftDetail() {
  const { id } = useParams();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // 'approve' | 'reject'
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(`/sellgo_v5_gift/${parseInt(id, 10)}`);
      if (res?.success && res.data) setGift(res.data);
      else setError(res?.error || 'تعذّر تحميل الهدية');
    } catch (e) { setError(e?.message || 'تعذّر تحميل الهدية'); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const doApprove = async () => {
    setDialog(null); setBusy(true);
    try {
      const res = await api.post('/sellgo_v5_gift_approve', { gift_id: parseInt(id, 10) });
      if (res?.success) { toast.success('تم صرف الهدية'); setGift(res.data); }
      else toast.error(res?.error || 'تعذّر الصرف');
    } catch (e) { toast.error(e?.message || 'تعذّر الصرف'); }
    setBusy(false);
  };

  const doReject = async () => {
    setDialog(null); setBusy(true);
    try {
      const res = await api.post('/sellgo_v5_gift_reject', { gift_id: parseInt(id, 10), reason: rejectReason.trim() });
      if (res?.success) { toast.success('تم رفض الهدية'); setGift(res.data); }
      else toast.error(res?.error || 'تعذّر الرفض');
    } catch (e) { toast.error(e?.message || 'تعذّر الرفض'); }
    setBusy(false);
  };

  if (loading) return <LoadingState message="جاري تحميل الهدية..." />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const canApprove = gift.state === 'submitted' && gift.can_dispense;

  return (
    <div className="max-w-2xl mx-auto pb-28" dir="rtl">
      <div className="px-4 pt-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-primary">
          <Gift className="w-5 h-5" />
          <h1 className="text-lg font-bold">{gift.name}</h1>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${STATE_COLOR[gift.state] || ''}`}>
          {gift.state_label || STATE_LABEL[gift.state] || gift.state}
        </span>
      </div>

      <div className="px-4 mt-3">
        <div className="bg-card border border-border rounded-2xl p-4 space-y-2">
          <Row icon={User} label="العميل" value={Array.isArray(gift.partner_id) ? gift.partner_id[1] : ''} />
          <Row icon={User} label="المندوب" value={Array.isArray(gift.rep_user_id) ? gift.rep_user_id[1] : ''} />
          <Row icon={Store} label="المخزن" value={Array.isArray(gift.warehouse_id) ? gift.warehouse_id[1] : ''} />
          {Array.isArray(gift.sale_order_id) && <Row icon={ShoppingCart} label="أمر البيع" value={gift.sale_order_id[1]} />}
          {gift.delivery_state && <Row icon={Truck} label="حالة التسليم" value={gift.delivery_state} />}
          {gift.reason && (
            <div className="text-sm pt-1 border-t border-border/60">
              <span className="text-muted-foreground">السبب: </span>
              <span className="text-foreground">{gift.reason}</span>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 mt-3">
        <p className="text-xs font-semibold text-muted-foreground mb-2">الأصناف</p>
        <div className="bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border">
          {(gift.lines || []).map((l) => (
            <div key={l.id} className="p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{Array.isArray(l.product_id) ? l.product_id[1] : '—'}</p>
                <p className="text-xs text-muted-foreground">{fmt2(l.quantity)} × {fmt2(l.unit_value)}</p>
              </div>
              <span className="text-sm font-bold text-foreground flex-shrink-0">{fmt2(l.value_subtotal)}</span>
            </div>
          ))}
          <div className="p-3 flex items-center justify-between bg-muted/40">
            <span className="text-sm font-semibold text-muted-foreground">قيمة الهدية</span>
            <span className="text-base font-bold text-primary">{fmt2(gift.amount_total)}</span>
          </div>
        </div>
      </div>

      {gift.state === 'rejected' && gift.reject_reason && (
        <div className="px-4 mt-3">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <p className="text-sm font-semibold text-red-800 mb-1">سبب الرفض</p>
            <p className="text-xs text-red-700">{gift.reject_reason}</p>
          </div>
        </div>
      )}

      {canApprove && (
        <div className="fixed bottom-0 right-0 left-0 bg-card border-t border-border px-4 py-3 z-30">
          <div className="max-w-2xl mx-auto flex gap-2">
            <Button onClick={() => setDialog('reject')} disabled={busy} variant="outline"
              className="flex-1 text-red-600 border-red-200 hover:bg-red-50 rounded-xl h-12 gap-2">
              <XCircle className="w-4 h-4" /> رفض
            </Button>
            <Button onClick={() => setDialog('approve')} disabled={busy}
              className="flex-1 bg-primary rounded-xl h-12 gap-2 font-semibold">
              <CheckCircle className="w-4 h-4" /> اعتماد وصرف
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>{dialog === 'approve' ? 'صرف الهدية' : 'رفض الهدية'}</AlertDialogTitle>
            <AlertDialogDescription>
              {dialog === 'approve'
                ? 'سيتم إنشاء أمر بيع بقيمة صفر وتسليمه من مخزن المندوب فوراً (مع التحقق من السقف الشهري).'
                : 'سيتم رفض طلب الهدية. يمكنك كتابة سبب الرفض.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {dialog === 'reject' && (
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3}
              placeholder="سبب الرفض (اختياري)..."
              className="w-full border border-border rounded-xl p-3 text-sm focus:border-primary focus:outline-none resize-none" />
          )}
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction onClick={dialog === 'approve' ? doApprove : doReject}
              className={dialog === 'approve' ? 'bg-primary text-white' : 'bg-destructive text-destructive-foreground hover:bg-destructive/90'}>
              {dialog === 'approve' ? 'صرف' : 'رفض'}
            </AlertDialogAction>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
